
import { Component, Vue } from "vue-property-decorator";
import vSelectControl from "@/components/vSelect.vue";
import vTable from "@/components/vTable.vue";
import FreeTrialUsageReportRepository from "@/utils/API/FreeTrialUsageReportRepository";
import {
  FreeTrialUsageReportEntryDto,
  FreeTrialUsageReportFiltersDto,
  FreeTrialUsageReportSummaryDto,
} from "@/models/FreeTrialUsageReport";

type TableCell = {
  component: string;
  props: Record<string, unknown>;
  events: Record<string, unknown>;
  html?: string;
  onClick: (id: number) => void;
};

type TableFilter = {
  name: string;
  value: string;
};

function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function createDefaultFilters(): FreeTrialUsageReportFiltersDto {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  return {
    trialStartDateFrom: formatDateForInput(startOfMonth),
    trialStartDateTo: formatDateForInput(today),
    trialStatus: "",
    companyName: "",
    userEmail: "",
    userName: "",
    sortBy: "trialStartDate",
    sortDirection: "desc",
    page: 1,
    pageSize: 10,
  };
}

function createDefaultSummary(): FreeTrialUsageReportSummaryDto {
  return {
    totalFreeTrialUsers: 0,
    activeTrialUsers: 0,
    inactiveTrialUsers: 0,
    convertedUsers: 0,
    expiredTrials: 0,
    conversionRate: 0,
    averageLoginsPerUser: 0,
  };
}

@Component({
  components: {
    vSelectControl,
    vTable,
  },
})
class FreeTrialUsageReport extends Vue {
  pageSize = 10;
  currentPage = 1;
  count = 0;
  totalCount = 0;
  tableResetKey = 0;
  columnFilters: TableFilter[] = [];
  draftFilters: FreeTrialUsageReportFiltersDto = createDefaultFilters();
  appliedFilters: FreeTrialUsageReportFiltersDto = createDefaultFilters();
  reportEntries: FreeTrialUsageReportEntryDto[] = [];
  summary: FreeTrialUsageReportSummaryDto = createDefaultSummary();
  isLoading = false;
  dateRangeError = "";

  mounted() {
    this.loadReport();
  }
  get conversionRateLabel(): string {
    return `${this.summary.conversionRate.toFixed(1)}%`;
  }

  get averageLoginsPerUserLabel(): string {
    return this.summary.averageLoginsPerUser.toFixed(2);
  }

  get trialStatusOptions() {
    return this.buildFilterOptions(["Active", "Expired", "Converted to Paid"]);
  }

  get selectedTrialStatusValue() {
    return this.findSelectedOption(this.trialStatusOptions, this.draftFilters.trialStatus);
  }

  get tableHeaders() {
    return [
      { name: "User", key: "UserName", searchable: true, searchString: "", sortable: false },
      { name: "Email", key: "EmailAddress", searchable: true, searchString: "", sortable: false },
      { name: "Company", key: "CompanyName", searchable: true, searchString: "", sortable: false },
      { name: "Trial Start", key: "TrialStartDate", searchable: false, searchString: "", sortable: false },
      { name: "Trial End", key: "TrialEndDate", searchable: false, searchString: "", sortable: false },
      { name: "Status", key: "TrialStatus", searchable: false, searchString: "", sortable: false },
      { name: "First Login", key: "FirstLoginDate", searchable: false, searchString: "", sortable: false },
      { name: "Last Login", key: "LastLoginDate", searchable: false, searchString: "", sortable: false },
      { name: "Total Logins", key: "TotalLogins", searchable: false, searchString: "", sortable: false },
      { name: "Active Days", key: "ActiveDays", searchable: false, searchString: "", sortable: false },
      { name: "Days Since Last Login", key: "DaysSinceLastLogin", searchable: false, searchString: "", sortable: false },
      { name: "Conversion Date", key: "ConversionDate", searchable: false, searchString: "", sortable: false },
    ];
  }

  get tableRows() {
    return this.reportEntries.map((entry) => ({
      e1: entry.userName,
      e2: entry.emailAddress,
      e3: entry.companyName,
      e4: this.formatShortDate(entry.trialStartDate),
      e5: this.formatShortDate(entry.trialEndDate),
      e6: this.buildStatusCell(entry.trialStatus),
      e7: this.formatDateTime(entry.firstLoginDate),
      e8: this.formatDateTime(entry.lastLoginDate),
      e9: entry.totalLogins,
      e10: entry.activeDays,
      e11: this.formatDays(entry.daysSinceLastLogin),
      e12: this.formatShortDate(entry.conversionDate),
      meta: {
        id: entry.restaurantId,
      },
    }));
  }

  loadReport() {
    this.isLoading = true;

    FreeTrialUsageReportRepository.getReport(this.buildRequestFilters())
      .then((response) => {
        const result = Array.isArray(response.data.result) ? response.data.result : [];
        this.count = typeof response.data.count === "number" ? response.data.count : result.length;
        this.totalCount = typeof response.data.totalCount === "number" ? response.data.totalCount : result.length;
        this.summary = response.data.summary || createDefaultSummary();
        this.reportEntries = result;
      })
      .catch(() => {
        this.reportEntries = [];
        this.count = 0;
        this.totalCount = 0;
        this.summary = createDefaultSummary();
        if (this.$snotify?.error) {
          this.$snotify.error("We couldn’t load the free trial usage report right now.");
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  generateReport() {
    if (!this.validateDateRange()) {
      return;
    }

    this.appliedFilters = {
      ...this.draftFilters,
    };
    this.currentPage = 1;
    this.tableResetKey += 1;
    this.loadReport();
  }

  handlePageChange(payload: unknown) {
    if (!Array.isArray(payload) || typeof payload[0] !== "number") {
      return;
    }

    this.currentPage = payload[0];
    this.columnFilters = Array.isArray(payload[2]) ? payload[2] : this.columnFilters;
    this.loadReport();
  }

  handleTableSearch(filters: TableFilter[]) {
    this.columnFilters = Array.isArray(filters) ? filters : [];
    this.currentPage = 1;
    this.loadReport();
  }

  validateDateRange(): boolean {
    this.dateRangeError = "";

    if (!this.draftFilters.trialStartDateFrom || !this.draftFilters.trialStartDateTo) {
      return true;
    }

    if (this.draftFilters.trialStartDateFrom <= this.draftFilters.trialStartDateTo) {
      return true;
    }

    this.dateRangeError = "From date cannot be later than To date.";
    if (this.$snotify?.error) {
      this.$snotify.error(this.dateRangeError);
    }

    return false;
  }

  async exportExcel() {
    try {
      const response = await FreeTrialUsageReportRepository.exportExcel(this.buildRequestFilters());
      this.downloadBlob(
        response.data,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        `free-trial-usage-report-${this.$moment().format("YYYYMMDDHHmmss")}.xlsx`
      );
    } catch {
      if (this.$snotify?.error) {
        this.$snotify.error("We couldn’t export the Excel report right now.");
      }
    }
  }

  downloadBlob(data: BlobPart, type: string, fileName: string) {
    const blob = new Blob([data], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  }

  buildRequestFilters(): FreeTrialUsageReportFiltersDto {
    const userNameFilter = this.columnFilters.find((filter) => filter.name === "UserName")?.value || "";
    const userEmailFilter = this.columnFilters.find((filter) => filter.name === "EmailAddress")?.value || "";
    const companyFilter = this.columnFilters.find((filter) => filter.name === "CompanyName")?.value || "";

    return {
      ...this.appliedFilters,
      userName: userNameFilter,
      userEmail: userEmailFilter,
      companyName: companyFilter,
      page: this.currentPage,
      pageSize: this.pageSize,
    };
  }

  buildFilterOptions(values: string[]) {
    return [
      { id: "", name: "All" },
      ...values.map((value) => ({ id: value, name: value })),
    ];
  }

  normalizeFilterValue(value: unknown): string {
    if (typeof value === "string") {
      return value === "All" ? "" : value;
    }

    if (value && typeof value === "object") {
      const option = value as { id?: unknown; name?: unknown };
      if (typeof option.id === "string") {
        return option.id === "All" ? "" : option.id;
      }

      if (typeof option.name === "string") {
        return option.name === "All" ? "" : option.name;
      }
    }

    return "";
  }

  findSelectedOption(options: Array<{ id: string; name: string }>, value: unknown) {
    const normalizedValue = this.normalizeFilterValue(value);
    return options.find((option) => option.id === normalizedValue) || options[0] || null;
  }
  buildStatusCell(trialStatus: string): TableCell {
    const statusClass = {
      "Active": "report-status-pill report-status-pill-followup",
      "Expired": "report-status-pill report-status-pill-cancelled",
      "Converted to Paid": "report-status-pill report-status-pill-accepted",
    }[trialStatus] || "report-status-pill report-status-pill-viewed";

    return {
      component: "span",
      props: {
        class: statusClass,
      },
      events: {},
      html: trialStatus,
      onClick: () => undefined,
    };
  }

  formatDateTime(value: string | null): string {
    return value ? this.$moment(value).format("MMM DD, YYYY h:mm A") : "N/A";
  }

  formatShortDate(value: string | null): string {
    return value ? this.$moment(value).format("MMM DD, YYYY") : "N/A";
  }

  formatDays(value: number | null): string {
    return value == null ? "N/A" : `${value} days`;
  }
}

export default FreeTrialUsageReport;
